/**
 * Connects the Usage statistics choice to HubSpot's visitor tracking.
 * Owns its browser loader and consent APIs; privacyTracking coordinates account
 * changes and the refresh needed to remove an already running SDK.
 */
import { getReviewedTrackingPage } from "@/utils/trackingLocation"

type HubSpotCommand = [string, ...unknown[]]
type HubSpotWindow = typeof window & {
	_hsq?: HubSpotCommand[]
	_hsp?: HubSpotCommand[]
	disableHubSpotCookieBanner?: boolean
}

enum HubSpotState {
	Idle,
	Loading,
	Active,
	Stopped,
}

const portalId = "8138613"
const browser = window as HubSpotWindow
const trackingCookies = ["hubspotutk", "__hstc", "__hssc", "__hssrc"]
const consentDenied = { analytics: false, advertisement: false, functionality: false }
let state = HubSpotState.Idle
let lastSentPath: string | undefined
let script: HTMLScriptElement | undefined

function sendConsent(allowed: boolean): void {
	browser._hsp?.push(["setHubSpotConsent", { ...consentDenied, analytics: allowed }])
}

function clearTrackingCookies(): void {
	// HubSpot shares these cookies with the public website. Clear all visible
	// scopes so a new account cannot inherit the previous visitor's identity.
	try {
		for (const name of trackingCookies) {
			for (const domain of ["", "; Domain=app.landerz.ca", "; Domain=landerz.ca"]) {
				document.cookie = `${name}=; Max-Age=0; Path=/${domain}; SameSite=Lax; Secure`
			}
		}
	} catch (error) {
		// Cookie restrictions must not prevent the remaining tools from stopping
		// or the coordinator from refreshing the page.
		console.warn("Could not clear HubSpot visitor cookies", error)
	}
}

function disableTracking(): void {
	// revokeCookieConsent can remove the do-not-track cookie. Keep the SDK's
	// runtime switch off too, and apply doNotTrack after cookie revocation.
	browser._hsq?.push(["setTrackingEnabled", false])
	browser._hsq?.push(["doNotTrack"])
}

export function applyHubSpotTrackingChoice(allowed: boolean): boolean {
	if (!allowed) {
		if (state !== HubSpotState.Idle) stopHubSpotTracking()
		return false
	}
	if (state === HubSpotState.Stopped) return false
	if (import.meta.env.VITE_ENABLE_HUBSPOT_TRACKING === "false" || window.location.hostname !== "app.landerz.ca") {
		return false
	}
	const page = getReviewedTrackingPage()
	if (!page) {
		if (state !== HubSpotState.Idle) stopHubSpotTracking()
		return false
	}
	if (state === HubSpotState.Loading) {
		// Only keep the latest path while the analytics child script downloads.
		// The standard loader's load event does not mean that child is ready.
		browser._hsq?.push(["setPath", page.pathname])
		lastSentPath = page.pathname
		return true
	}
	if (state === HubSpotState.Active) {
		if (page.pathname !== lastSentPath) {
			browser._hsq?.push(["setPath", page.pathname])
			browser._hsq?.push(["trackPageView"])
			lastSentPath = page.pathname
		}
		return true
	}

	state = HubSpotState.Loading
	lastSentPath = page.pathname
	browser.disableHubSpotCookieBanner = true
	browser._hsp = []
	browser._hsq = []
	// HubSpot's consent module also supplies the visitor cookies. Keep it, but
	// let our own banner own the choice and deny advertising/functionality.
	browser._hsp.push([
		"addPrivacyConsentListener",
		() => {
			if (state === HubSpotState.Stopped) disableTracking()
		},
	])
	sendConsent(true)
	// setPath controls the report's page name, not every SDK field. HubSpot also
	// reads the URL, title and referrer; that collection needs review before merge.
	browser._hsq.push(["setPath", page.pathname])
	browser._hsq.push(["doNotTrack", { track: true }])
	// The SDK processes this listener after its own initial page view. Later
	// navigation can then use manual page views without duplicating that first one.
	browser._hsq.push([
		"addUserTokenListener",
		() => {
			if (state === HubSpotState.Loading) state = HubSpotState.Active
		},
	])

	script = document.createElement("script")
	script.id = "hs-script-loader"
	script.async = true
	script.defer = true
	script.referrerPolicy = "no-referrer"
	script.src = `https://js.hs-scripts.com/${portalId}.js`
	script.onerror = () => stopHubSpotTracking()
	document.head.appendChild(script)
	return true
}

export function stopHubSpotTracking(): void {
	if (state === HubSpotState.Idle || state === HubSpotState.Stopped) return
	state = HubSpotState.Stopped
	// Before initialization these are ordinary queues; after initialization their
	// push methods dispatch immediately. Discard pending grants in both cases.
	if (browser._hsq) browser._hsq.length = 0
	if (browser._hsp) browser._hsp.length = 0
	// Disable sends before changing vendor consent: its listeners may run
	// synchronously, and revocation itself can otherwise send a tracking event.
	browser._hsq?.push(["setTrackingEnabled", false])
	sendConsent(false)
	browser._hsp?.push(["revokeCookieConsent"])
	browser._hsq?.push(["doNotTrack"])
	clearTrackingCookies()
	if (script) {
		script.onerror = null
		script.remove()
	}
	// Removing the loader cannot undo an executed child script. The coordinator
	// refreshes the page after withdrawal; the stop commands protect that interval.
}
